<template>
    <div class="container">
        <schoolAndDate :schoolName="schoolName" :dateTime="dateTime" />
        <div class="wrap" ref="viewport">
            <transition name="page-turn-fade" mode="out-in">
                <div class="meal-list-wrap" :key="currentPageKey">
                    <div class="meal-list" v-for="(section, sectionIndex) in currentPage.sections" :key="section.sectionKey + '-' + sectionIndex">
                        <div class="meal-block">
                            <div class="meal-item meal-head">
                                <div class="meal-title meal-col-dish"><span>菜品</span></div>
                                <div class="meal-title meal-col-price"><span>价格(元/人)</span></div>
                            </div>
                            <div class="meal-item" v-for="row in section.rows" :key="row.rowKey">
                                <div class="meal-content meal-col-dish"><span>{{ row.dishName }}</span></div>
                                <div class="meal-content meal-col-price"><span>{{ row.priceText }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <div class="measure-layer" aria-hidden="true">
                <div class="meal-list-wrap measure-wrap">
                    <div class="meal-list" v-for="(section, sectionIndex) in displaySections" :key="section.sectionKey + '-measure'">
                        <div class="meal-block">
                            <div class="meal-item meal-head" :ref="'measureHead-' + sectionIndex">
                                <div class="meal-title meal-col-dish"><span>菜品</span></div>
                                <div class="meal-title meal-col-price"><span>价格(元/人)</span></div>
                            </div>
                            <div class="meal-item" v-for="(row, rowIndex) in section.rows" :key="row.rowKey" :ref="'measureRow-' + sectionIndex + '-' + rowIndex">
                                <div class="meal-content meal-col-dish"><span>{{ row.dishName }}</span></div>
                                <div class="meal-content meal-col-price"><span>{{ row.priceText }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PageTurnControl class="page-turn-control-position" :current="currentPageIndex + 1" :total="pages.length" theme="meal" @prev="goPrevPage" @next="goNextPage" />
        </div>
    </div>
</template>

<script>
import { paginateMeasuredSections } from '../assets/js/contentPaging';
import { canAutoTurnPage, getNextPageIndex, getPrevPageIndex } from '../assets/js/pageTurn';
import { buildNutritiousDiningOpenRows } from '../assets/js/nutritiousDiningOpenRows';
import { formatTime } from '../assets/js/common';
import PageTurnControl from './PageTurnControl.vue';
import schoolAndDate from './schoolAndDate.vue';

export default {
    props: {
        deptId: {
            type: String,
            default: '',
        },
        outletsId: {
            type: String,
            default: '',
        },
        dateTime: {
            type: String,
            default: '',
        },
        schoolName: {
            type: String,
            default: '',
        },
    },
    components: { PageTurnControl, schoolAndDate },
    data() {
        return {
            openInfo: {},
            pages: [],
            currentPageIndex: 0,
            pageTimer: null,
        };
    },
    computed: {
        displayRows() {
            return buildNutritiousDiningOpenRows(this.openInfo);
        },
        displaySections() {
            var rows = this.displayRows.map(function (row, index) {
                return {
                    rowKey: 'open-row-' + index,
                    dishName: row && row.dishName ? row.dishName : '',
                    priceText: row && row.priceText ? row.priceText : '',
                };
            });

            return [
                {
                    sectionKey: 'open',
                    sectionHeader: { startDate: '', endDate: '' },
                    rows: rows,
                },
            ];
        },
        currentPage() {
            return this.pages[this.currentPageIndex] || { sections: [] };
        },
        currentPageKey() {
            return 'page-' + this.currentPageIndex;
        },
        autoplayDuration() {
            var query = this.$route && this.$route.query ? this.$route.query : {};
            var autoplay = query && query.autoplay ? parseInt(query.autoplay, 10) : 0;

            if (isNaN(autoplay) || autoplay <= 0) {
                return 0;
            }

            return autoplay;
        },
    },
    watch: {
        openInfo: {
            deep: true,
            handler: function () {
                this.rebuildPages();
            },
        },
        '$route.query.autoplay': function () {
            this.startPageTimer();
        },
    },
    mounted() {
        this.fetchOpenInfo();
        window.addEventListener('resize', this.rebuildPages);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.rebuildPages);
        this.stopPageTimer();
    },
    methods: {
        fetchOpenInfo() {
            var query = this.$route && this.$route.query ? this.$route.query : {};
            var validPeriodDate = query && query.validPeriodDate ? String(query.validPeriodDate) : '';

            if (!validPeriodDate) {
                validPeriodDate = formatTime(new Date(), 'yyyy-MM-dd');
            }

            var param = {
                canteenId: this.deptId,
                validPeriodDate: validPeriodDate,
            };

            this.$get(this.$api.getNutritiousDiningOpenScreenInfo, param).then((res) => {
                this.openInfo = res && res.data && typeof res.data === 'object' ? res.data : {};
            });
        },
        stopPageTimer() {
            if (this.pageTimer) {
                clearInterval(this.pageTimer);
                this.pageTimer = null;
            }
        },
        startPageTimer() {
            var vm = this;
            this.stopPageTimer();
            if (!canAutoTurnPage(this.pages.length, this.autoplayDuration)) {
                return;
            }
            this.pageTimer = setInterval(function () {
                vm.currentPageIndex = getNextPageIndex(vm.currentPageIndex, vm.pages.length);
            }, this.autoplayDuration);
        },
        restartPageTimer() {
            this.stopPageTimer();
            this.startPageTimer();
        },
        goPrevPage() {
            this.currentPageIndex = getPrevPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
        },
        goNextPage() {
            this.currentPageIndex = getNextPageIndex(this.currentPageIndex, this.pages.length);
            this.restartPageTimer();
        },
        getRefElement(refName) {
            var refValue = this.$refs[refName];
            return Array.isArray(refValue) ? refValue[0] : refValue;
        },
        getMeasuredSection(section, sectionIndex) {
            var headEl = this.getRefElement('measureHead-' + sectionIndex);
            var rows = section.rows.map(function (row, rowIndex) {
                var rowEl = this.getRefElement('measureRow-' + sectionIndex + '-' + rowIndex);
                return {
                    rowKey: row.rowKey,
                    measuredHeight: rowEl ? rowEl.offsetHeight : 0,
                    payload: row,
                };
            }, this);

            return {
                sectionKey: section.sectionKey,
                sectionHeader: section.sectionHeader,
                headerHeight: headEl ? headEl.offsetHeight : 0,
                rows: rows,
            };
        },
        getMeasureWrapInset() {
            var measureWrap = this.$el ? this.$el.querySelector('.measure-wrap') : null;
            var styles;
            if (!measureWrap) {
                return 0;
            }
            styles = window.getComputedStyle(measureWrap);
            return (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
        },
        getSectionGap() {
            var firstSection = this.$el ? this.$el.querySelector('.measure-wrap .meal-list') : null;
            var styles;
            if (!firstSection) {
                return 0;
            }
            styles = window.getComputedStyle(firstSection);
            return parseFloat(styles.marginBottom) || 0;
        },
        rebuildPages() {
            this.$nextTick(function () {
                var viewport = this.$refs.viewport;
                var pageInset = this.getMeasureWrapInset();
                var sectionGap = this.getSectionGap();
                var measuredSections = this.displaySections.map(function (section, index) {
                    return this.getMeasuredSection(section, index);
                }, this);

                this.pages = paginateMeasuredSections({
                    maxPageHeight: viewport ? viewport.clientHeight : 0,
                    pageInset: pageInset,
                    sectionGap: sectionGap,
                    sections: measuredSections,
                }).map(function (page) {
                    return {
                        pageIndex: page.pageIndex,
                        sections: page.sections.map(function (section) {
                            return {
                                sectionKey: section.sectionKey,
                                sectionHeader: section.sectionHeader,
                                continued: section.continued,
                                rows: section.rows.map(function (row) {
                                    return row.payload;
                                }),
                            };
                        }),
                    };
                });

                if (this.currentPageIndex >= this.pages.length) {
                    this.currentPageIndex = 0;
                }

                this.startPageTimer();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
$fontColor: #0A7B23;
$borderColor: #59C06F;

.container {
    width: 100%;
}

.wrap {
    position: relative;
    height: vh(785);
    background: #b2f5c2;
    overflow: hidden;
    border-radius: 3px;
    font-weight: bold;
}

.meal-list-wrap {
    padding: vh(12) vh(18) vh(58);
}

.meal-list {
    margin-bottom: vh(10);
}

.meal-block {
    border: 1px solid $borderColor;
    color: $fontColor;
    border-radius: vh(10);
    overflow: hidden;
}

.meal-item {
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid $borderColor;
}

.meal-item:last-child {
    border-bottom: 0;
}

.meal-head {
    background: #8EDE9F;
}

.meal-title,
.meal-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: vh(10) vh(12);
    box-sizing: border-box;
    word-break: break-all;
}

.meal-title {
    font-size: vh(14);
}

.meal-content {
    font-size: vh(16);
}

.meal-col-dish {
    width: 70%;
    border-right: 1px solid $borderColor;
    justify-content: flex-start;
    text-align: left;
}

.meal-col-price {
    width: 30%;
}

.measure-layer {
    position: absolute;
    left: -9999px;
    top: 0;
    width: 100%;
    height: auto;
    opacity: 0;
    pointer-events: none;
}

.page-turn-control-position {
    position: absolute;
    left: 50%;
    bottom: vh(8);
    z-index: 8;
    transform: translateX(-50%);
}

.page-turn-fade-enter-active,
.page-turn-fade-leave-active {
    transition: opacity 0.3s ease;
}

.page-turn-fade-enter,
.page-turn-fade-leave-to {
    opacity: 0;
}
</style>
